// components/Students/EditStudentPage.js
import React, { useEffect, useMemo, useState } from "react";
import StudentForm from "./StudentForm";
import apiClient from "../../utils/apiClient";
import { EMPTY_FEE, courseListFee, feeFromEnrolment } from "../../utils/enrolmentFee";
import { useNavigate, useParams } from "react-router-dom";
import { Spinner, Alert } from "react-bootstrap";
import { useHasStudentModule } from "../../contexts/PlanContext";
import { staffDocumentsApi } from "./profile/documentsApi";
import useStudentDocuments from "./profile/useStudentDocuments";

const idOf = (ref) => String((ref && typeof ref === "object" ? ref._id : ref) || "");

const initialForm = {
  name: "",
  email: "",
  mobile: "",
  dob: "",
  gender: "",
  address: "",
  course: "",
  batch: "",
  degree: "",
  degreeBranch: "",
  college: "",
  percentage: "",
  passout: "",
  experience: "",
  workHistory: [],
  totalFees: "",
  paidFees: "",
  pendingFees: "",
  placement: "Yes",
  refName: "",
  refNumber: "",
  comments: "",
};

const EditStudentPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const advancedProfile = useHasStudentModule()("advancedProfile");

  const [form, setForm] = useState(initialForm);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [saving, setSaving] = useState(false);
  const [courseOptions, setCourseOptions] = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [initialAdditionalCourses, setInitialAdditionalCourses] = useState([]);
  const [courseAssignments, setCourseAssignments] = useState([]);
  const [initialPrimaryFee, setInitialPrimaryFee] = useState(EMPTY_FEE);
  const [primaryStored, setPrimaryStored] = useState(null);
  const [feeError, setFeeError] = useState(null);

  // The documents are stored the moment they are chosen — the record exists.
  const documentsApi = useMemo(() => staffDocumentsApi(id), [id]);
  const docs = useStudentDocuments(documentsApi, advancedProfile);

  // Fetch student info and courses
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setErr("");
      try {
        const [{ data: sData }, { data: cData }] = await Promise.all([
          apiClient.get(`/students/${id}`),
          apiClient.get("/courses/list"),
        ]);
        const student = sData.student;
        setForm({
          ...initialForm,
          ...student,
          course: student.course?._id || "",
          batch: student.batch?._id || "",
          percentage: student.percentage ?? "",
          workHistory: Array.isArray(student.workHistory) ? student.workHistory : [],
        });
        // `fee` is the course's own fee, filled in when a course is picked.
        setCourseOptions((cData.courses || []).map(c => ({ value: c._id, label: c.name, fee: courseListFee(c) })));

        const assignments = sData.courseAssignments || [];
        setCourseAssignments(assignments);
        const primaryCourseId = idOf(student.course);

        // The primary course's fee comes from ITS enrolment. Student.totalFees
        // is the roll-up of every course, and reading it here put the
        // additional courses' fees into the primary course on every save.
        const primaryCA = primaryCourseId
          ? assignments.find((a) => idOf(a.courseId) === primaryCourseId) || null
          : null;
        if (primaryCA) {
          setInitialPrimaryFee(feeFromEnrolment(primaryCA));
          setPrimaryStored({ totalFees: primaryCA.totalFees || 0, paidFees: primaryCA.paidFees || 0 });
        } else if (primaryCourseId) {
          // A student from before enrolment rows: the course lives on the
          // student alone, the way the student record folds it in.
          setInitialPrimaryFee(feeFromEnrolment({ totalFees: student.totalFees }));
          setPrimaryStored({ totalFees: student.totalFees || 0, paidFees: student.paidFees || 0 });
        }

        // Every other enrolment is an additional course. Matched on the course,
        // not only the flag, so a row whose flag disagrees with Student.course
        // is never offered as both.
        const additional = assignments
          .filter((a) => !a.isPrimary && idOf(a.courseId) !== primaryCourseId)
          .map((a) => {
            const courseId = idOf(a.courseId);
            const fee = feeFromEnrolment(a);
            return {
              courseId,
              batchId: idOf(a.batchId),
              fee,
              saved: { courseId, fee, totalFees: a.totalFees || 0, paidFees: a.paidFees || 0 },
            };
          });
        setInitialAdditionalCourses(additional);
      } catch {
        setErr("Failed to load student details.");
      }
      setLoading(false);
    };
    fetchData();
  }, [id]);

  // Fetch batches for selected course
  const fetchBatches = async (courseId) => {
    if (!courseId) return setBatchOptions([]);
    try {
      const { data } = await apiClient.get("/batches/list", { params: { course: courseId } });
      setBatchOptions((data.batches || []).map(b => ({ value: b._id, label: b.name })));
    } catch {
      setBatchOptions([]);
    }
  };

  // Refetch batches when course is loaded or changes
  useEffect(() => {
    if (form.course) fetchBatches(form.course);
  }, [form.course]);

  const handleEdit = async (formData, additionalCourses = []) => {
    setSaving(true);
    setFeeError(null);
    try {
      const payload = { ...formData };
      if (!payload.course) payload.course = null;
      if (!payload.batch) payload.batch = null;
      // Include additional courses in payload
      payload.additionalCourses = additionalCourses.filter(ac => ac.courseId);
      await apiClient.put(`/students/${id}`, payload);
      setToast({ show: true, message: "Student updated successfully!", type: "success" });
      setTimeout(() => navigate(`/students/${id}`), 1200);
    } catch (error) {
      // A 409 names the clash ("…already uses this mobile number"); a refused
      // fee names its course, and is shown there too.
      const message = error?.response?.data?.message || "Failed to update student.";
      setToast({ show: true, message, type: "error" });
      const courseId = error?.response?.data?.courseId;
      if (courseId) setFeeError({ courseId, message });
    }
    setSaving(false);
  };

  if (loading)
    return <div className="text-center my-5"><Spinner animation="border" /></div>;
  if (err)
    return <Alert variant="danger" className="my-5 text-center">{err}</Alert>;

  return (
    <StudentForm
      initialData={form}
      courseOptions={courseOptions}
      batchOptions={batchOptions}
      loading={saving}
      toast={toast}
      setToast={setToast}
      mode="edit"
      onSubmit={handleEdit}
      onCancel={() => navigate(-1)}
      onChangeCourse={fetchBatches}
      initialAdditionalCourses={initialAdditionalCourses}
      courseAssignments={courseAssignments}
      initialPrimaryFee={initialPrimaryFee}
      primaryStored={primaryStored}
      feeError={feeError}
      advancedProfile={advancedProfile}
      docs={docs}
    />
  );
};

export default EditStudentPage;
